import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { api } from './client'
import type { components } from './types'

export type TenantDevice = components['schemas']['TenantDeviceInfo']
export type TenantDevicesResponse = components['schemas']['TenantDevicesResponse']
export type DeviceBindRequest = components['schemas']['DeviceBindRequest']
export type TenantDeviceUpdate = components['schemas']['TenantDeviceUpdate']

/** GET /api/v1/devices — список девайсов тенанта с лимитом лицензии */
export function useDevicesList() {
  return useQuery({
    queryKey: ['devices', 'list'],
    queryFn: async () => {
      const response = await api.get<TenantDevicesResponse>('/devices')
      return response.data
    },
    refetchInterval: 30_000,
  })
}

/** POST /api/v1/devices/{id}/bind — привязать девайс к машине */
export function useBindDevice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { deviceId: string; vehicleId: string }) => {
      const body: DeviceBindRequest = { vehicle_id: vars.vehicleId }
      const response = await api.post<TenantDevice>(`/devices/${vars.deviceId}/bind`, body)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
    },
  })
}

/** POST /api/v1/devices/{id}/unbind — отвязать девайс от машины */
export function useUnbindDevice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (deviceId: string) => {
      const response = await api.post<TenantDevice>(`/devices/${deviceId}/unbind`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
    },
  })
}

/** PATCH /api/v1/devices/{id} — переключить VPN-режим девайса. */
export function useSetDeviceVpn() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { deviceId: string; vpnEnabled: boolean }) => {
      const body: TenantDeviceUpdate = { vpn_enabled: vars.vpnEnabled }
      const response = await api.patch<TenantDevice>(`/devices/${vars.deviceId}`, body)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
    },
  })
}
